// noinspection UnnecessaryLocalVariableJS

import {defineStore} from 'pinia';
import {v4 as uuidv4} from 'uuid';
import { useMainStore } from '@/stores/useMainStore.js';
import {
    berechneAlter,
    berechneBeitrag, 
    DatumISO,
    formatiereBeitrag,
    formatiereInitialerBeitrag,
    getRechnerVariante,
    isValidDate,
    isValidDateInPast
} from '../helpers/hilfsfunktionen';
import {getKrankenkassen} from "../api/KrankenkassenService.js";
import {getMakler} from "../api/MaklerService.js";
import {useTarifinfosStore} from "./tarifinfos.js";
import { useDokStore } from './dok.js';

export const useRechnerStore = defineStore('rechner', {
    state: () => ({
        items: [],
        rechner:"",
        versicherungsbeginn: "",
        aok: 0,
        aokName: '',
        gesamt: "",
        vnabweichend: 0,
        bankabweichend: "",
        bankabweichendValidation: 0,
        kundenentscheid: 0,
        abweichendText: "",
        vertragsdokumentegeladen: false,
        beratungsart: 0,
        kenntnisnahme: false,
        vollstaendig: false,
        eweSozialdaten: false,
        eweKontakt: false,
        eweWillenserklaerung: false,
        step0ok: false,
        step1ok: false,
        step2ok: false,
        step3ok: false,
        step4ok: false,
        stepStatus: 0,
        rabattall: false,
        checkZx2: false,
        vndaten: {
            id: 'initial',
            alter: -1,
            anrede: '',
            titel: '',
            vorname: '',
            nachname: '',
            aoknummer: '',
            krankenkassenName: '',
            geburtstag: '',
            strasse: '',
            hausnummer: '',
            plz: '',
            ort: '',
            land: '',
            email: '',
            telefon: '',
            staatsangehoerigkeit: ''
        },
        bankdaten: {
            id: 'initialBank',
            anrede: '',
            titel: '',
            vorname: '',
            nachname: '',
            strasse: '',
            hausnummer: '',
            plz: '',
            ort: '',
            land: '',
            iban: ''
        },
        ktobank: '',
        vertragsnummer: 0,
        secretstring: '',
        teaserStationaer: false,
        teaserAusland: true,
        teaserZahn: false,
        hasDownload: false,
        krankenkassenListe: [],
        antragAbgeschickt: false,
        // ARNF: Geburtsdatum der ältesten Person (separat vom VN)
        geburtsdatumAeltestePerson: '',
    }),
    actions: {
        initializeStore(parvariante) {
            if (this.items.length === 0) {
                this.items.push(this.createDefaultItem(parvariante));
                this.calculateAll();
            }
        },
        cleanUpStore() {
            this.vnabweichend = 0;
            this.bankabweichend = "";
            this.bankabweichendValidation = 0;
            this.vndaten = { id: 'initial', alter: -1, anrede : '', titel : '', vorname : '', nachname: '', aoknummer: '', krankenkassenName:'',geburtstag: '', strasse: '', hausnummer: '', plz: '', ort: '', land: 'DE', email: '', telefon: '', staatsangehoerigkeit: '' };
            this.bankdaten = { id: 'initialBank', alter: -1, anrede : '', titel : '', vorname : '', nachname: '', strasse: '', hausnummer: '', plz: '', ort: '', land: '', iban:'' };
            this.items.length = 0;
            this.items.push(this.createDefaultItem());
            this.aok = 0;
            this.aokName = '';
            this.versicherungsbeginn = "";
            this.gesamt = "";
            this.meldung = "Bitte den Versicherungsbeginn auswählen";
            this.vnabweichend = 0;            
            this.kundenentscheid = 0;
            this.abweichendText =  "";
            this.resetVertragsdokumentDownloadStatus();
            this.beratungsart = 0;
            this.kenntnisnahme = false;
            this.vollstaendig = false;
            this.eweSozialdaten = false;
            this.eweKontakt = false;
            this.eweWillenserklaerung = false;
            this.step0ok = false;
            this.step1ok = false;
            this.step2ok = false;
            this.step3ok = false;
            this.step4ok = false;
            this.stepStatus = 0;
            this.rabattall = false;
            this.ktobank= '';
            this.teaserStationaer= false;
            this.teaserAusland= true;
            this.teaserZahn= false;
            this.antragAbgeschickt = false;
            // ARNF: Geburtsdatum der ältesten Person zurücksetzen
            this.geburtsdatumAeltestePerson = '';
            this.calculateAll();
        },
        resetVertragsdokumentDownloadStatus() {
            this.hasDownload = false;
            this.vertragsdokumentegeladen = false;
            const dokStore = useDokStore();
            dokStore.hasDownload = false;
            dokStore.isDownloading = false;
            dokStore.errorMessage = '';
        },
        async fetchKrankenkassen() {
            try {
                const response = await getKrankenkassen()
                if (response.status === 200) {
                    const rawList = response.data['data']['hits']['hits'];
                    const krankenkassen = rawList.map(item => item['_source']['gkvname']);
                    const freieHeilfuersorge = 'Freie Heilfürsorge';
                    const schonVorhanden = krankenkassen.some(
                        (name) => String(name).toLowerCase() === freieHeilfuersorge.toLowerCase()
                    );
                    this.krankenkassenListe = schonVorhanden
                        ? krankenkassen
                        : [...krankenkassen, freieHeilfuersorge];
                }
            } catch (error) {
                console.error('Error fetching krankenkassen:', error);
            }
        },
        createDefaultItem() {
            return {
                id: uuidv4(),
                option: 0,
                alter: -1,
                anrede: '',
                titel: '',
                vorname: '',
                nachname: '',
                aoknummer: '',
                krankenkassenName: '',
                geburtstag: '',
                tarif1: 0,
                tarif1beitrag: 0,
                tarif1beitragStr: '- €',
                tarif2: 0,
                tarif2beitrag: 0,
                tarif2beitragStr: '- €',
                tarif3: 0,
                tarif3beitrag: 0,
                tarif3beitragStr: '- €',
                tarif4: 0,
                tarif4beitrag: 0,
                tarif4beitragStr: '- €',
                tarif5: 0,
                tarif5beitrag: 0,
                tarif5beitragStr: '- €',
                tarif6: 0,
                tarif6beitrag: 0,
                tarif6beitragStr: '- €',
                tarif7: 0,
                tarif7beitrag: 0,
                tarif7beitragStr: '- €',
                tarif8: 0,
                tarif8beitrag: 0,
                tarif8beitragStr: '- €',
                beitrag: 0,
                beitragStr: '- €'
            };
        },
        addItemBasedOnLast() {
            if (this.items.length > 0) {
                const lastItem = {...this.items[this.items.length - 1]};
                lastItem.id = uuidv4();
                lastItem.geburtstag = '';
                lastItem.tarif1beitrag = 0;
                lastItem.tarif1beitragStr = '- €';
                lastItem.tarif2beitrag = 0;
                lastItem.tarif2beitragStr = '- €';
                lastItem.tarif3beitrag = 0;
                lastItem.tarif3beitragStr = '- €';
                lastItem.tarif4beitrag = 0;
                lastItem.tarif4beitragStr = '- €';
                lastItem.tarif5beitrag = 0;
                lastItem.tarif5beitragStr = '- €';
                lastItem.tarif6beitrag = 0;
                lastItem.tarif6beitragStr = '- €';
                lastItem.tarif7beitrag = 0;
                lastItem.tarif7beitragStr = '- €';
                lastItem.tarif8beitrag = 0;
                lastItem.tarif8beitragStr = '- €';
                lastItem.beitrag = 0;
                lastItem.beitragStr = '';
                lastItem.anrede = '';
                lastItem.titel = '';
                lastItem.vorname = '';
                lastItem.nachname = '';
                lastItem.plz = '';
                lastItem.ort = '';
                lastItem.strasse = '';
                lastItem.hausnummer = '';
                lastItem.land = '';
                lastItem.aoknummer = '';
                lastItem.krankenkassenName = '';
                this.items.push(lastItem);
                this.calculateAll();
            }
        },
        deleteItem(id) {
            this.items = this.items.filter(item => item.id !== id);
            this.calculateAll();
        },
        setVn(item) {
            // Schritt-2-Felder (Adresse, Kontakt) immer erhalten für bessere UX
            const erhalteneFelder = {
                strasse: this.vndaten.strasse || '',
                hausnummer: this.vndaten.hausnummer || '',
                plz: this.vndaten.plz || '',
                ort: this.vndaten.ort || '',
                land: this.vndaten.land || 'DE',
                email: this.vndaten.email || '',
                telefon: this.vndaten.telefon || '',
                staatsangehoerigkeit: this.vndaten.staatsangehoerigkeit || ''
            };
            
            if (item) {
                // VP als VN: Personendaten aus Item, Schritt-2-Felder erhalten
                this.vnabweichend = 0
                this.vndaten = {
                    ...item,
                    ...erhalteneFelder
                }
            } else {
                // Abweichender VN (vn0): Prüfen ob bereits vn0 war
                const sameAsVN0 = this.vndaten.id === 'vn0';
                
                this.vnabweichend = 1
                this.vndaten = {
                    id: 'vn0',
                    alter: -1,
                    // Personendaten nur erhalten wenn bereits vn0 war
                    anrede: sameAsVN0 ? this.vndaten.anrede : '',
                    titel: sameAsVN0 ? this.vndaten.titel : '',
                    vorname: sameAsVN0 ? this.vndaten.vorname : '',
                    nachname: sameAsVN0 ? this.vndaten.nachname : '',
                    aoknummer: sameAsVN0 ? this.vndaten.aoknummer : '',
                    krankenkassenName: sameAsVN0 ? this.vndaten.krankenkassenName : '',
                    geburtstag: sameAsVN0 ? this.vndaten.geburtstag : '',
                    // Schritt-2-Felder immer erhalten
                    ...erhalteneFelder
                };
            }
        },
        setBankdaten(item) {
            const oldIBAN = this.bankdaten.iban
            if (item) {                
                this.bankdaten = {
                    ...item,                   
                    iban: oldIBAN
                }
            } else {
                this.bankdaten = {
                    id: 'vn0',
                    alter: -1,
                    anrede: '',
                    titel: '',
                    vorname: '',
                    nachname: '',
                    aoknummer: '',
                    krankenkassenName: '',
                    geburtstag: '',
                    strasse: '',
                    hausnummer: '',
                    plz: '',
                    ort: '',
                    land: 'DE',
                    iban: oldIBAN
                };
            }
        },
        aktualisiereBankdatenMitVNDaten() {
            this.bankdaten.id = this.vndaten.id
            this.bankdaten.anrede = this.vndaten.anrede
            this.bankdaten.titel = this.vndaten.titel
            this.bankdaten.vorname = this.vndaten.vorname
            this.bankdaten.nachname = this.vndaten.nachname
            this.bankdaten.strasse = this.vndaten.strasse
            this.bankdaten.hausnummer = this.vndaten.hausnummer
            this.bankdaten.plz = this.vndaten.plz
            this.bankdaten.ort = this.vndaten.ort
            this.bankdaten.land = this.vndaten.land
            
        },
        setTarif(id, tarifValue) {
            // MATOMO-DEAKTIVIERT (TODO: reaktivieren) - sämtliche _paq.push-Aufrufe in dieser Funktion sind deaktiviert
            const item = this.items.find(item => item.id === id);
            if (item) {
                if (tarifValue === 1) {
                    //ZVS
                    if (item.tarif1 === 1) {
                        item.tarif1 = 0;
                        item.tarif2 = 0;  
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Abwahl_ZVS','Abwahl_ZVS'])                      
                    } else {
                        item.tarif1 = 1;
                        item.tarif2 = 0;
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Auswahl_ZVS','Auswahl_ZVS'])
                    }
                } else if (tarifValue === 2) {
                    //ZVP
                    if (item.tarif2 === 1) {
                        item.tarif1 = 0;
                        item.tarif2 = 0;   
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Abwahl_ZVP','Abwahl_ZVP'])                    
                    } else {
                        item.tarif1 = 0;
                        item.tarif2 = 1;
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Auswahl_ZVP','Auswahl_ZVP'])
                    }
                } else if (tarifValue === 3) {
                    //ZX2
                    if (item.tarif3 === 1) {
                        item.tarif3 = 0;
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Abwahl_ZX2','Abwahl_ZX2'])
                    } else {
                        item.tarif3 = 1;
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Auswahl_ZX2','Auswahl_ZX2'])
                    }
                } else if (tarifValue === 4) {
                    //S1
                    if (item.tarif4 === 1) {
                        item.tarif4 = 0;
                        item.tarif5 = 0;
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Abwahl_S1','Abwahl_S1'])
                    } else {
                        item.tarif4 = 1;
                        item.tarif5 = 0;
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Auswahl_S1','Auswahl_S1'])
                    }
                } else if (tarifValue === 5) {
                    //S2
                    if (item.tarif5 === 1) {
                        item.tarif4 = 0;
                        item.tarif5 = 0;
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Abwahl_S2','Abwahl_S2'])
                    } else {
                        item.tarif4 = 0;
                        item.tarif5 = 1;
                        //window._paq?.push(['trackEvent', 'Kombirechner', '4YOU_Auswahl_S2','Auswahl_S2'])
                    }
                } else if (tarifValue === 6) {
                    //AR20
                    if (item.tarif6 === 1) {
                        item.tarif6 = 0;                        
                        //window._paq?.push(['trackEvent', 'Kombirechner', 'Abwahl AR20','Abwahl_AR20'])
                    } else {
                        item.tarif6 = 1;
                        //window._paq?.push(['trackEvent', 'Kombirechner', 'Auswahl AR20','Auswahl_AR20'])
                    }
                } else if (tarifValue === 7) {
                    //ARNS
                    if (item.tarif7 === 1) {
                        item.tarif7 = 0;                        
                        //window._paq?.push(['trackEvent', 'Kombirechner', 'Abwahl ARNS','Abwahl_ARNS'])
                    } else {
                        item.tarif7 = 1;
                        //window._paq?.push(['trackEvent', 'Kombirechner', 'Auswahl ARNS','Auswahl_ARNS'])
                    }
                } else if (tarifValue === 8) {
                    //ARNS
                    if (item.tarif8 === 1) {
                        item.tarif8 = 0;                        
                        //window._paq?.push(['trackEvent', 'Kombirechner', 'Abwahl ARNF','Abwahl_ARNF'])
                    } else {
                        item.tarif8 = 1;
                        //window._paq?.push(['trackEvent', 'Kombirechner', 'Auswahl ARNF','Auswahl_ARNF'])
                    }
                }

                item.hasTarif = this.isTarifValid(item.id)
                this.calculateAll();
            }
        },
        setItem(id, newItem) {
            const index = this.items.findIndex(item => item.id === id);
            if (index !== -1) {
                this.items[index] = newItem;
                this.calculateAll();
            }
        },
        isTarifValid(id) {
            const item = this.items.find(item => item.id === id);
            if (item) {
                return item.tarif1 === 1 || item.tarif2 === 1 || item.tarif3 === 1 || item.tarif4 === 1 || item.tarif5 === 1 || item.tarif6 === 1 || item.tarif7 === 1 || item.tarif8 === 1;
            }
            return false;
        },
        calculateAll() {
            let gesamtBeitrag = 0;
            let gesamtBeitragPerson = 0;
            let gesamtRechenbar = true;
            
            // Hole rechnerType und kanal aus MainStore für API-Zugriff
            const mainStore = useMainStore();
            const rechnerType = mainStore.dataAttributes.rechner;
            const kanal = mainStore.dataAttributes.kanal;
            
            if (isValidDate(this.versicherungsbeginn)) {
                this.items.forEach(item => {
                    gesamtBeitragPerson = 0;
                    const lokGeb = item.geburtstag;
                    if (isValidDateInPast(lokGeb)) {
                        // AR20/ARN: Taggenau rechnen, Kombi: Nicht taggenau
                        //const taggenau = (rechnerType === 'ar20' || rechnerType === 'arn');
                        const taggenau = (rechnerType === 'arn');
                        const lokAlter = berechneAlter(item.geburtstag, this.versicherungsbeginn, taggenau);
                        item.alter = lokAlter;
                        let lokBeitrag = berechneBeitrag(lokAlter, 1, this.versicherungsbeginn, rechnerType, kanal);
                        if (item.tarif1 === 1) {
                            item.tarif1beitrag = lokBeitrag;
                            gesamtBeitrag = gesamtBeitrag + lokBeitrag;
                            gesamtBeitragPerson = gesamtBeitragPerson + lokBeitrag;
                        } else {
                            item.tarif1beitrag = 0;
                        }
                        item.tarif1beitragStr = formatiereBeitrag(lokBeitrag);

                        lokBeitrag = berechneBeitrag(lokAlter, 2, this.versicherungsbeginn, rechnerType, kanal);
                        if (item.tarif2 === 1) {
                            item.tarif2beitrag = lokBeitrag;
                            gesamtBeitrag = gesamtBeitrag + lokBeitrag;
                            gesamtBeitragPerson = gesamtBeitragPerson + lokBeitrag;
                        } else {
                            item.tarif2beitrag = 0;
                        }
                        item.tarif2beitragStr = formatiereBeitrag(lokBeitrag);

                        lokBeitrag = berechneBeitrag(lokAlter, 3, this.versicherungsbeginn, rechnerType, kanal);
                        if (item.tarif3 === 1) {
                            item.tarif3beitrag = lokBeitrag;
                            gesamtBeitrag = gesamtBeitrag + lokBeitrag;
                            gesamtBeitragPerson = gesamtBeitragPerson + lokBeitrag;
                        } else {
                            item.tarif3beitrag = 0;
                        }
                        item.tarif3beitragStr = formatiereBeitrag(lokBeitrag);

                        lokBeitrag = berechneBeitrag(lokAlter, 4, this.versicherungsbeginn, rechnerType, kanal);
                        if (item.tarif4 === 1) {
                            item.tarif4beitrag = lokBeitrag;
                            gesamtBeitrag = gesamtBeitrag + lokBeitrag;
                            gesamtBeitragPerson = gesamtBeitragPerson + lokBeitrag;
                        } else {
                            item.tarif4beitrag = 0;
                        }
                        item.tarif4beitragStr = formatiereBeitrag(lokBeitrag);

                        lokBeitrag = berechneBeitrag(lokAlter, 5, this.versicherungsbeginn, rechnerType, kanal);
                        if (item.tarif5 === 1) {
                            item.tarif5beitrag = lokBeitrag;
                            gesamtBeitrag = gesamtBeitrag + lokBeitrag;
                            gesamtBeitragPerson = gesamtBeitragPerson + lokBeitrag;
                        } else {
                            item.tarif5beitrag = 0;
                        }
                        item.tarif5beitragStr = formatiereBeitrag(lokBeitrag);

                        lokBeitrag = berechneBeitrag(lokAlter, 6, this.versicherungsbeginn, rechnerType, kanal);
                        if (item.tarif6 === 1) {
                            item.tarif6beitrag = lokBeitrag;
                            gesamtBeitrag = gesamtBeitrag + lokBeitrag;
                            gesamtBeitragPerson = gesamtBeitragPerson + lokBeitrag;
                        } else {
                            item.tarif6beitrag = 0;
                        }
                        item.tarif6beitragStr = formatiereBeitrag(lokBeitrag);

                        // ARNS: Beitrag basiert auf Alter der versicherten Person (wie andere Tarife)
                        lokBeitrag = berechneBeitrag(lokAlter, 7, this.versicherungsbeginn, rechnerType, kanal);
                        if (item.tarif7 === 1) {
                            item.tarif7beitrag = lokBeitrag;
                            gesamtBeitrag = gesamtBeitrag + lokBeitrag;
                            gesamtBeitragPerson = gesamtBeitragPerson + lokBeitrag;
                        } else {
                            item.tarif7beitrag = 0;
                        }
                        item.tarif7beitragStr = formatiereBeitrag(lokBeitrag);

                        // ARNF: Beitrag basiert auf Alter der ältesten Person (NICHT der versicherten Person)
                        if (item.tarif8 === 1) {
                            if (this.geburtsdatumAeltestePerson && isValidDateInPast(this.geburtsdatumAeltestePerson)) {
                                const alterAelteste = berechneAlter(this.geburtsdatumAeltestePerson, this.versicherungsbeginn, taggenau);
                                lokBeitrag = berechneBeitrag(alterAelteste, 8, this.versicherungsbeginn, rechnerType, kanal);
                                item.tarif8beitrag = lokBeitrag;
                                gesamtBeitrag = gesamtBeitrag + lokBeitrag;
                                gesamtBeitragPerson = gesamtBeitragPerson + lokBeitrag;
                                item.tarif8beitragStr = formatiereBeitrag(lokBeitrag);
                            } else {
                                // ARNF gewählt aber kein gültiges Geburtsdatum der ältesten Person - InitialPreis anzeigen
                                const tarifinfosStore = useTarifinfosStore();
                                const variante = getRechnerVariante(rechnerType, kanal);
                                const preis8 = variante && tarifinfosStore.isLoaded 
                                    ? tarifinfosStore.getInitialerPreis(variante, 'ARNF') 
                                    : 0;
                                item.tarif8beitrag = preis8 || 0;
                                item.tarif8beitragStr = formatiereInitialerBeitrag(preis8);
                            }
                        } else {
                            item.tarif8beitrag = 0;
                            item.tarif8beitragStr = formatiereBeitrag(0);
                        }

                        item.beitrag = gesamtBeitragPerson;
                        item.beitragStr = formatiereBeitrag(gesamtBeitragPerson);
                    } else {
                        this.gesamt = "Bitte immer ein Geburtsdatum angeben";
                        
                        // Verwende initiale Preise aus API wenn verfügbar (mit "ab" Prefix)
                        const tarifinfosStore = useTarifinfosStore();
                        const variante = getRechnerVariante(rechnerType, kanal);
                        
                        if (variante && tarifinfosStore.isLoaded) {
                            // Hole initiale Preise für alle Tarife
                            const preis1 = tarifinfosStore.getInitialerPreis(variante, 'ZVS');
                            const preis2 = tarifinfosStore.getInitialerPreis(variante, 'ZVP');
                            const preis3 = tarifinfosStore.getInitialerPreis(variante, 'ZX2');
                            const preis4 = tarifinfosStore.getInitialerPreis(variante, 'S1');
                            const preis5 = tarifinfosStore.getInitialerPreis(variante, 'S2');
                            const preis6 = tarifinfosStore.getInitialerPreis(variante, 'AR20');
                            const preis7 = tarifinfosStore.getInitialerPreis(variante, 'ARNS');
                            
                            item.tarif1beitrag = preis1 || 0;
                            item.tarif1beitragStr = formatiereInitialerBeitrag(preis1);
                            item.tarif2beitrag = preis2 || 0;
                            item.tarif2beitragStr = formatiereInitialerBeitrag(preis2);
                            item.tarif3beitrag = preis3 || 0;
                            item.tarif3beitragStr = formatiereInitialerBeitrag(preis3);
                            item.tarif4beitrag = preis4 || 0;
                            item.tarif4beitragStr = formatiereInitialerBeitrag(preis4);
                            item.tarif5beitrag = preis5 || 0;
                            item.tarif5beitragStr = formatiereInitialerBeitrag(preis5);
                            item.tarif6beitrag = preis6 || 0;
                            item.tarif6beitragStr = formatiereInitialerBeitrag(preis6);
                            item.tarif7beitrag = preis7 || 0;
                            item.tarif7beitragStr = formatiereInitialerBeitrag(preis7);
                            
                            // ARNF: Sonderbehandlung - Beitrag basiert auf ältester Person
                            if (item.tarif8 === 1 && this.geburtsdatumAeltestePerson && isValidDateInPast(this.geburtsdatumAeltestePerson)) {
                                const taggenau = (rechnerType === 'ar20' || rechnerType === 'arn');
                                const alterAelteste = berechneAlter(this.geburtsdatumAeltestePerson, this.versicherungsbeginn, taggenau);
                                const lokBeitrag = berechneBeitrag(alterAelteste, 8, this.versicherungsbeginn, rechnerType, kanal);
                                item.tarif8beitrag = lokBeitrag;
                                item.tarif8beitragStr = formatiereBeitrag(lokBeitrag);
                            } else {
                                const preis8 = tarifinfosStore.getInitialerPreis(variante, 'ARNF');
                                item.tarif8beitrag = preis8 || 0;
                                item.tarif8beitragStr = formatiereInitialerBeitrag(preis8);
                            }
                        } else {
                            // Fallback: Setze alles auf 0
                            item.tarif1beitrag = 0;
                            item.tarif1beitragStr = '- €';
                            item.tarif2beitrag = 0;
                            item.tarif2beitragStr = '- €';
                            item.tarif3beitrag = 0;
                            item.tarif3beitragStr = '- €';
                            item.tarif4beitrag = 0;
                            item.tarif4beitragStr = '- €';
                            item.tarif5beitrag = 0;
                            item.tarif5beitragStr = '- €';
                            item.tarif6beitrag = 0;
                            item.tarif6beitragStr = '- €';
                            item.tarif7beitrag = 0;
                            item.tarif7beitragStr = '- €';
                            item.tarif8beitrag = 0;
                            item.tarif8beitragStr = '- €';
                        }
                        
                        gesamtBeitrag = 0;
                        gesamtRechenbar = false;
                    }
                });  // for each
                if (gesamtRechenbar === false) {
                    gesamtBeitrag = 0;
                    this.gesamt = 'Bitte immer ein Geburtsdatum eingeben';
                } else {
                    this.gesamt = formatiereBeitrag(gesamtBeitrag);
                }
            } else {
                // Kein Versicherungsbeginn: Zeige initiale Preise mit "ab" Prefix
                this.gesamt = "Bitte den Versicherungsbeginn auswählen";
                
                const tarifinfosStore = useTarifinfosStore();
                const variante = mainStore.currentVariante;
                
                if (variante && tarifinfosStore.isLoaded) {
                    this.items.forEach(item => {
                        const preis1 = tarifinfosStore.getInitialerPreis(variante, 'ZVS');
                        const preis2 = tarifinfosStore.getInitialerPreis(variante, 'ZVP');
                        const preis3 = tarifinfosStore.getInitialerPreis(variante, 'ZX2');
                        const preis4 = tarifinfosStore.getInitialerPreis(variante, 'S1');
                        const preis5 = tarifinfosStore.getInitialerPreis(variante, 'S2');
                        const preis6 = tarifinfosStore.getInitialerPreis(variante, 'AR20');
                        const preis7 = tarifinfosStore.getInitialerPreis(variante, 'ARNS');
                        const preis8 = tarifinfosStore.getInitialerPreis(variante, 'ARNF');
                        
                        item.tarif1beitrag = preis1 || 0;
                        item.tarif1beitragStr = formatiereInitialerBeitrag(preis1);
                        item.tarif2beitrag = preis2 || 0;
                        item.tarif2beitragStr = formatiereInitialerBeitrag(preis2);
                        item.tarif3beitrag = preis3 || 0;
                        item.tarif3beitragStr = formatiereInitialerBeitrag(preis3);
                        item.tarif4beitrag = preis4 || 0;
                        item.tarif4beitragStr = formatiereInitialerBeitrag(preis4);
                        item.tarif5beitrag = preis5 || 0;
                        item.tarif5beitragStr = formatiereInitialerBeitrag(preis5);
                        item.tarif6beitrag = preis6 || 0;
                        item.tarif6beitragStr = formatiereInitialerBeitrag(preis6);
                        item.tarif7beitrag = preis7 || 0;
                        item.tarif7beitragStr = formatiereInitialerBeitrag(preis7);
                        item.tarif8beitrag = preis8 || 0;
                        item.tarif8beitragStr = formatiereInitialerBeitrag(preis8);
                    });
                }
            }

        },
        hatStationaer() {
            return this.items.some(item => item.tarif4 === 1 || item.tarif5 === 1);
        },
        hatS1() {
            return this.items.some(item => item.tarif4 === 1);
        },
        hatS2() {
            return this.items.some(item => item.tarif5 === 1);
        },
        hatZVS() {
            return this.items.some(item => item.tarif1 === 1);
        },
        hatZVP() {
            return this.items.some(item => item.tarif2 === 1);
        },
        hatZX2() {
            return this.items.some(item => item.tarif3 === 1);
        },
        hatAR20() {
            return this.items.some(item => item.tarif6 === 1);
        },
        hatARNS() {
            return this.items.some(item => item.tarif7 === 1);
        },
        hatARNF() {
            return this.items.some(item => item.tarif8 === 1);
        },
        hatARN() {
            return this.items.some(item => item.tarif7 === 1 || item.tarif8 === 1);
        },
        VNIstVP() {
            const ergebnis = this.items.some(item => item.id === this.vndaten.id);
            return ergebnis;
        },
        getVNVersicherteTarife() {
            const ergebnis = [];
            const item = this.items.find(item => item.id === this.vndaten.id);
            if (item) {
                // Variante dynamisch ermitteln basierend auf Kanal
                const mainStore = useMainStore();
                let variante = mainStore.kanal === "meinevigo" ? "V" : "A";
                // ARN (ARNS/ARNF): Immer Variante "V", unabhängig von Kanal und Vertriebsweg
                if (mainStore.rechner === "arn") {
                    variante = "V";
                }
                
                if (item.tarif1===1) { ergebnis.push({ tarifbezeichnung: "ZVS", Tarifvariante: variante }); }
                if (item.tarif2===1) { ergebnis.push({ tarifbezeichnung: "ZVP", Tarifvariante: variante }); }
                if (item.tarif3===1) { ergebnis.push({ tarifbezeichnung: "ZX2", Tarifvariante: variante }); }
                if (item.tarif4===1) { ergebnis.push({ tarifbezeichnung: "S1", Tarifvariante: variante }); }
                if (item.tarif5===1) { ergebnis.push({ tarifbezeichnung: "S2", Tarifvariante: variante }); }
                if (item.tarif6===1) { ergebnis.push({ tarifbezeichnung: "AR20", Tarifvariante: variante }); }
                if (item.tarif7===1) { ergebnis.push({ tarifbezeichnung: "ARNS", Tarifvariante: variante }); }
                if (item.tarif8===1) { ergebnis.push({ tarifbezeichnung: "ARNF", Tarifvariante: variante }); }
            }
            return ergebnis;
        },
        KVNrDoppelt(par) {
            // "AOK" ist immer erlaubt (wird vom Special-Validator verwendet)
            if (par === "AOK") return false;
            // Leerer String ist erlaubt
            if (par === "") return false;
            
            // Zähle, wie oft die AOK-Nummer in allen Items vorkommt
            const count = this.items.filter(item => item.aoknummer === par).length;
            
            if (count > 1) {
                // Mehr als 1x in Items → Duplikat
                return true;
            } else if (count === 1) {
                // Genau 1x in Items: Prüfe, ob VN auch die Nummer hat
                if (this.VNIstVP() === false) {  // VN ist NICHT selbst versichert
                    if (this.vndaten.aoknummer === par) {
                        // VN hat die gleiche Nummer → Duplikat
                        return true;
                    }
                }
            }
            // Keine Dublette
            return false;
        },
        
        /**
         * Gibt den Text für die Erklärung zur Versicherungsfähigkeit zurück
         * basierend auf dem Kanal
         * @returns {string} Text für die Checkbox-Label
         */
        gibTextVersicherungsfaehigkeit() {
            const mainStore = useMainStore();
            if (mainStore.kanal === 'vigoselect') {
                return 'Die folgende/n Person/en ist/sind bei der AOK Rheinland/Hamburg versichert oder hat/haben einen Mitgliedsantrag gestellt.';
            }
            return 'Die folgende/n Person/en ist/sind bei einer gesetzlichen Krankenkasse oder im Rahmen freier Heilfürsorge versichert.';
        },

    },
    persist: {
        storage: sessionStorage
    }
});
